import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { axiosInstance } from '@/lib/axios'

/** Rodzaje zadań — odpowiednik DriverRequiredActionTypes po stronie serwera. */
export type DriverRequiredActionType =
  | 'missingContract'
  | 'violationsOutOfDate'
  | 'foreignActivityWithoutTrip'
  | 'timesheetNotPrepared'
  | 'timesheetOutOfDate'
  | 'taxExemptExpired'
  | 'taxExemptUnder26Missing'
  | 'taxExemptAgeUnknown'

/** Waga niesie pilność, nie rodzaj — kolor znacznika bierze się właśnie stąd. */
export type DriverRequiredActionSeverity = 'critical' | 'warning' | 'info'

export type DriverRequiredActionItem = {
  type: DriverRequiredActionType
  severity: DriverRequiredActionSeverity
  /** Miejsce w kolejności rozliczania: umowa 1 → naruszenia 2 → delegacje 3 → ewidencja 4. */
  step: number
  /** Liczba pozycji, których dotyczy zadanie (dziś: dni za granicą bez delegacji). */
  count: number | null
  month: string | null
  /** Powód nieaktualnej ewidencji — TimesheetModificationType. */
  cause: number | null
  /** Data związana z zadaniem — dziś: dzień 26. urodzin przy uldze dla młodych. */
  date: string | null
}

export type DriverRequiredActions = {
  driverId: number
  /** Null, gdy przy kierowcy są wyłącznie pozycje ulgi (taxExemptItems). */
  severity: DriverRequiredActionSeverity | null
  nextStep: number
  items: DriverRequiredActionItem[]
  /** Ulga dla młodych — osobna ikonka, poza kolejnością rozliczania. */
  taxExemptItems: DriverRequiredActionItem[]
}

type Props = {
  companyId: string | undefined
  /** Miesiąc w formacie YYYY-MM — ten sam, który wybiera moduł kierowców. */
  month: string | undefined
  enabled?: boolean
}

/**
 * Zadania czekające przy kierowcach w wybranym miesiącu — jedno żądanie na całą listę.
 *
 * Osobne zapytanie niż `basicDrivers`, bo lista kierowców nie może czekać na sygnały:
 * panel renderuje się na każdym ekranie modułu i ma być widoczny od razu, a znaczniki
 * dochodzą chwilę później.
 */
export function useQueryDriverRequiredActions({
  companyId,
  month,
  enabled = true,
}: Props) {
  const [year, monthNumber] = (month ?? '').split('-')

  return useQuery<DriverRequiredActions[]>({
    queryKey: ['driverRequiredActions', String(companyId ?? ''), month ?? ''],
    enabled: enabled && !!companyId && !!year && !!monthNumber,
    staleTime: MINUTE_IN_MS * 2,
    queryFn: async () => {
      const res = await axiosInstance.post(
        `/api/${companyId}/drivers/requiredActions`,
        {
          driverIds: [],
          year: Number(year),
          month: Number(monthNumber),
        },
      )
      return res.data ?? []
    },
  })
}

/**
 * Znaczniki zadań są migawką — po akcji, która coś załatwia albo unieważnia (przeliczenie,
 * zapis delegacji, zapis harmonogramu), trzeba je przeładować, inaczej panel pokazuje stan
 * sprzed zmiany do czasu odświeżenia przeglądarki.
 *
 * Unieważniamy cały prefiks, bez rozbijania na miesiące: zamontowane jest i tak tylko
 * zapytanie o aktualnie wybrany miesiąc.
 */
export function useInvalidateDriverRequiredActions() {
  const queryClient = useQueryClient()

  return React.useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'driverRequiredActions',
      }),
    [queryClient],
  )
}
